<template>
  <div class="maintain-directory-archived-component">
    <div id="maintain-directory-archived-header">
      <div class="clearfix header-panel">
        <div class="header">
          <span class="notice-header">{{ $t('directory.maintainArchivedTitle') }}</span>
        </div>
      </div>
      <div class="directory-archived-search-panel row">
        <div class="multi-select-block multi-select-block--building">
          <label>{{ $t('directory.label.building') }}</label>
          <HKLSelect
            :value="searchParams.building"
            :options="buildingOptions"
            :showSearch="true"
            placeholder=""
            @change="updateBuildingInQuery"
          />
        </div>
        <div class="multi-select-block multi-select-block--type">
          <label>{{ $t('directory.label.view') }}</label>
          <HKLSelect
            :value="searchParams.type"
            :options="viewTypes"
            :showSearch="true"
            placeholder=""
            @change="updateTypeInQuery"
          />
        </div>
        <div
          v-show="displayFloorSelect"
          class="multi-select-block multi-select-block--floor"
        >
          <label>{{ $t('directory.label.floor') }}</label>
          <HKLSelect
            :value="searchParams.floor"
            :options="floorList"
            :showSearch="true"
            placeholder=""
            @change="updateFloorInQuery"
          />
        </div>
        <div
          v-if="displayLobbySelect"
          class="multi-select-block multi-select-block--floor"
        >
          <label>{{ $t('directory.label.floor') }}</label>
          <InputField
            class="maintain-input-field"
            :dirtyCheck="false"
            :value="searchCompanyParams.floor"
            @updateValue="val => updateLobbyFloorInQuery(val)"
          />
        </div>
        <div class="multi-select-block multi-select-block--officeName">
          <label>{{ $t('directory.label.officeName') }}</label>
          <InputField
            class="maintain-input-field"
            :dirtyCheck="false"
            :value="searchCompanyParams.engName"
            @updateValue="val => updateOfficeNameInQuery(val)"
          />
        </div>
        <div
          v-show="displayFloorSelect"
          class="multi-select-block multi-select-block--premises"
        >
          <label>{{ $t('directory.label.premises') }}</label>
          <InputField
            class="maintain-input-field"
            :dirtyCheck="false"
            :value="searchCompanyParams.premises"
            @updateValue="val => updatePremisesInQuery(val)"
          />
        </div>
        <div class="multi-select-block multi-select-block--date">
          <label>{{ $t('directory.label.effectiveDate') }}</label>
          <DatePicker
            :calendarButton="true"
            calendarButtonIcon="fa fa-calendar"
            :value="filterParams.effectiveDate ? new Date(filterParams.effectiveDate) : null"
            @selected="val => filterParams.effectiveDate = val"
            @cleared="() => filterParams.effectiveDate = null"
          />
        </div>
        <div class="multi-select-block multi-select-block--date">
          <label>{{ $t('directory.label.removeDate') }}</label>
          <DatePicker
            :calendarButton="true"
            calendarButtonIcon="fa fa-calendar"
            :value="filterParams.removeDate ? new Date(filterParams.removeDate) : null"
            @selected="val => filterParams.removeDate = val"
            @cleared="() => filterParams.removeDate = null"
          />
        </div>
        <div class="multi-select-block multi-select-block--date">
          <label>{{ $t('directory.label.publishedDate') }}</label>
          <DatePicker
            :calendarButton="true"
            calendarButtonIcon="fa fa-calendar"
            :value="filterParams.publishedAt ? new Date(filterParams.publishedAt) : null"
            @selected="val => filterParams.publishedAt = val"
            @cleared="() => filterParams.publishedAt = null"
          />
        </div>

        <div class="multi-select-block">
          <label>&nbsp;</label>
          <button
            class="btn btn--darkblue"
            :disabled="!canShowDirectory"
            @click="showDirectory"
          >{{ $t('action.showDirectory') }}</button>
          <Popover
            :text="$t('directory.hint.onlyShowLevel1')"
            position="top-left"
            :width="300"
          />
        </div>
      </div>
      <DirectoryArchivedTable
        ref="directoryArchivedTable"
      />
    </div>
  </div>
</template>

<script>
import { ref, unref } from 'vue-demi'
import DatePicker from '@/controls/DatePicker'
import InputField from '@/controls/InputField'
import HKLSelect from '@/controls/Select/HKLSelect.vue'
import Popover from '@/controls/Popover'
import { DIR_TYPE } from '@/modules/directories/consts'
import { useStoreGetters } from '@/store/index'
import { useUserBuildingOpts } from '../tenantProjects/hook'
import DirectoryArchivedTable from './DirectoryArchivedTable'

export default {
  name: 'DirectoryArchived',
  components: {
    DatePicker,
    InputField,
    HKLSelect,
    Popover,
    DirectoryArchivedTable,
  },
  setup () {
    const searchParams = ref({
      building: null,
      type: null,
      floor: null,
    })
    const floorList = ref([])
    const { buildingOptions } = useUserBuildingOpts()
    const { allBuildings } = useStoreGetters('buildings', ['allBuildings'])
    const updateFloorList = () => {
      const selectedBuilding = unref(allBuildings).find(building => building.code === unref(searchParams).building)
      floorList.value = selectedBuilding ? selectedBuilding.floor : []
    }
    return {
      floorList,
      searchParams,
      buildingOptions,
      updateFloorList,
    }
  },
  data () {
    return {
      viewTypes: [DIR_TYPE.LOBBY, DIR_TYPE.FLOOR],
      displayFloorSelect: false,
      displayLobbySelect: false,
      searchCompanyParams: {},
      filterParams: {},
    }
  },
  computed: {
    canShowDirectory () {
      return this.checkSearchParams(this.searchParams)
    },
  },
  async mounted () {
    const routeQuery = this.$router.currentRoute.query
    if (routeQuery.building) {
      this.updateBuildingInQuery(routeQuery.building)
    }
    if (routeQuery.type) {
      this.updateTypeInQuery(routeQuery.type)
    }
    if (routeQuery.type === DIR_TYPE.FLOOR && routeQuery.floor) {
      this.updateFloorInQuery(routeQuery.floor)
    }
    if (routeQuery.premises) {
      this.updatePremisesInQuery(routeQuery.premises)
    }
    if (routeQuery.lobbyFloor) {
      this.updateLobbyFloorInQuery(routeQuery.lobbyFloor)
    }
    if (routeQuery.engName) {
      this.updateOfficeNameInQuery(routeQuery.engName)
    }
    if (routeQuery.effectiveDate) {
      this.filterParams.effectiveDate = routeQuery.effectiveDate
    }
    if (routeQuery.removeDate) {
      this.filterParams.removeDate = routeQuery.removeDate
    }
    if (routeQuery.publishedAt) {
      this.filterParams.publishedAt = routeQuery.publishedAt
    }
    if (this.checkSearchParams(this.searchParams)) {
      await this.showDirectory()
    }
  },
  methods: {
    checkSearchParams (searchParams) {
      return searchParams.type && searchParams.building && (searchParams.type !== DIR_TYPE.FLOOR || searchParams.floor)
    },
    updateTypeInQuery (type) {
      this.$set(this.searchParams, 'type', type)
      if (type !== DIR_TYPE.FLOOR) {
        this.$delete(this.searchParams, 'floor')
      }
      this.displayFloorSelect = this.searchParams.type === DIR_TYPE.FLOOR && !!this.searchParams.building
      this.displayLobbySelect = this.searchParams.type === DIR_TYPE.LOBBY && !!this.searchParams.building
      if (this.displayFloorSelect) {
        this.updateFloorList()
        if (this.searchCompanyParams.floor) {
          delete this.searchCompanyParams.floor
        }
      }
      if (this.displayLobbySelect && this.searchCompanyParams.premises) {
        delete this.searchCompanyParams.premises
      }
    },
    updateBuildingInQuery (building) {
      this.$set(this.searchParams, 'building', building)
      this.displayFloorSelect = this.searchParams.type === DIR_TYPE.FLOOR && !!this.searchParams.building
      if (this.displayFloorSelect) {
        this.updateFloorList()
      }
    },
    updateFloorInQuery (floor) {
      this.$set(this.searchParams, 'floor', floor)
    },
    updateOfficeNameInQuery (name) {
      this.searchCompanyParams.engName = (name || '').trim()
    },
    updateLobbyFloorInQuery (lobbyFloor) {
      this.searchCompanyParams.floor = (lobbyFloor || '').trim()
    },
    updatePremisesInQuery (premises) {
      this.searchCompanyParams.premises = (premises || '').trim()
    },
    updateDate (val) {
      this.filterParams.date = val
    },
    async showDirectory () {
      await this.$refs.directoryArchivedTable.search(this.searchParams, this.filterParams, this.searchCompanyParams)
      this.$refs.directoryArchivedTable.resetSortHeader()
    },
  },
}
</script>

<style lang="scss" scoped src="styles/viewAll.scss"></style>
<style lang="scss" scoped src="styles/dashboard/style.scss"></style>
<style lang="scss" scoped src="styles/dashboard/datatable.scss"></style>
<style lang="scss" scoped>
  @import '~styles/popup';

  .directory-archived-search-panel {
    width: auto;
    padding:15px 0;
    box-shadow: 0 0 4px 0 rgba(172, 172, 172, 0.5);
    background-color: #ffffff;
    margin: 15px;
    align-items: center;
    .multi-select-block {
      margin-left: 15px;
      width: 237px;
      label {
        height: 20px;
        font-size: 14px;
        font-weight: bold;
        text-transform: uppercase;
        margin-bottom: 9px;
      }
    }
    .multi-select-block--building {
      width: 120px;
    }
    .multi-select-block--type {
      width: 120px;
    }
    .multi-select-block--floor{
      width: 120px;
    }
    .multi-select-block--officeName {
      width: 250px;
    }
    .multi-select-block--premises {
      width: 180px;
    }
    .multi-select-block--date {
      width: 180px;
      & ::v-deep .vdp-datepicker {
        width: auto;
      }
    }
    & ::v-deep .maintain-input-field input {
      margin-bottom: 0;
      width: 100%;
    }
    & ::v-deep .hkl-select {
      width: 100%;
    }
  }
</style>
